import app from 'flarum/forum/app';
import { guideBusinesses } from './BusinessesPage';
import Page from 'flarum/common/components/Page';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';

/**
 * Location guides: /data-hub/guides (all guides) and /data-hub/guides/:slug (one guide).
 * A guide's page body and CSS come from GET /datahub/guides/{slug}; its CSS is scoped under .DHGuide
 * (guides/convert.py), so it can't change the rest of the forum.
 */

let list = null; // the guides, loaded once
function loadList() {
  if (list === null) {
    list = false;
    app.request({
      method: 'GET',
      url: app.forum.attribute('apiUrl') + '/datahub/guides',
      errorHandler: () => {}
    }).then(r => {
      list = r && r.guides || [];
      m.redraw();
    }).catch(() => {
      list = [];
    });
  }
  return list || null;
}
const go = (e, url) => {
  if (e.ctrlKey || e.metaKey || e.shiftKey) return;
  e.preventDefault();
  m.route.set(url);
};

/** Links inside a guide that point at Voucha (e.g. "Discuss this", /t/canada) open without a page load */
function internalLinks(e) {
  const a = e.target.closest && e.target.closest('a[href]');
  if (!a || a.target === '_blank') return;
  const href = a.getAttribute('href') || '';
  if (/^\/(?!\/)/.test(href)) go(e, href);
}
function switcher(current) {
  const guides = loadList();
  return m("div", {
    className: "DHGuideBar"
  }, m("a", {
    className: "DataHubPub-back",
    href: "/data-hub",
    onclick: e => go(e, '/data-hub')
  }, m("i", {
    className: "fas fa-arrow-left"
  }), " Data Hub"), guides && guides.length ? m("nav", {
    className: "DHGuideBar-links",
    "aria-label": "Location guides"
  }, guides.map(g => m("a", {
    className: 'DHGuideBar-link' + (g.slug === current ? ' is-active' : ''),
    href: '/data-hub/guides/' + g.slug,
    "aria-current": g.slug === current ? 'page' : undefined,
    onclick: e => go(e, '/data-hub/guides/' + g.slug)
  }, g.shape ? m("svg", {
    className: "DHGuideBar-shape",
    viewBox: g.shape.viewBox,
    "aria-hidden": "true"
  }, m("path", {
    d: g.shape.d
  })) : null, g.name))) : null);
}
export class GuidePage extends Page {
  oninit(vnode) {
    super.oninit(vnode);
    this.bodyClass = 'DataHubFull';
    this.load(m.route.param('slug'));
  }
  onbeforeupdate(vnode) {
    super.onbeforeupdate && super.onbeforeupdate(vnode);
    const slug = m.route.param('slug');
    if (slug !== this.slug) this.load(slug);
  }
  load(slug) {
    this.slug = slug;
    this.guide = undefined;
    const g = (loadList() || []).find(x => x.slug === slug);
    app.setTitle(g ? g.title : 'Location guide');
    app.request({
      method: 'GET',
      url: app.forum.attribute('apiUrl') + '/datahub/guides/' + encodeURIComponent(slug),
      errorHandler: () => {}
    }).then(r => {
      if (this.slug !== slug) return;
      this.guide = r && r.guide || null;
      if (this.guide) app.setTitle(this.guide.title);
    }).catch(() => {
      if (this.slug === slug) this.guide = null;
    }).finally(() => m.redraw());
    if (typeof window !== 'undefined') window.scrollTo(0, 0);
  }
  view() {
    const g = this.guide;
    return m("div", {
      className: "DataHubFull-page DHGuidePage"
    }, m("div", {
      className: "DHGuidePage-top"
    }, switcher(this.slug)), g === undefined ? m(LoadingIndicator, null) : !g ? m("div", {
      className: "DHGuidePage-missing"
    }, m("p", {
      className: "DHMuted"
    }, "There's no guide here."), m("a", {
      className: "Button",
      href: "/data-hub/guides",
      onclick: e => go(e, '/data-hub/guides')
    }, "All location guides")) : m("div", {
      className: "DHGuide",
      onclick: internalLinks
    }, m.trust('<style>' + g.css + '</style>'), this.body(g)));
  }
}

/**
 * The guide's HTML with members' businesses as one more numbered section, just before its sources (the guide is
 * <main class="wrap"> … <section class="sources"> …; when it isn't shaped like that, the section goes at the end).
 */
GuidePage.prototype.body = function (g) {
  const entry = (loadList() || []).find(x => x.slug === this.slug);
  const html = String(g.html || '');
  const open = '<main class="wrap">';
  const at = html.indexOf('<section class="sources">');
  if (!entry || !entry.tag) return m.trust(html);
  const name = entry.name || 'this country';
  if (html.indexOf(open) !== 0 || at < 0) return [m.trust(html), m("div", {
    className: "wrap"
  }, guideBusinesses(entry.tag, name, null))];
  const numbers = (html.match(/<div class="eyebrow">(\d+) ·/g) || []).map(x => parseInt(x.replace(/\D/g, ''), 10));
  const next = numbers.length ? Math.max(...numbers) + 1 : null;
  return m("main", {
    className: "wrap"
  }, m.trust(html.slice(open.length, at)), guideBusinesses(entry.tag, name, next), m.trust(html.slice(at).replace(/<\/main>\s*$/, '')));
};
export class GuidesIndexPage extends Page {
  oninit(vnode) {
    super.oninit(vnode);
    this.bodyClass = 'DataHubFull';
    app.setTitle('Location guides');
  }
  view() {
    const guides = loadList();
    return m("div", {
      className: "DataHubFull-page DHGuidePage"
    }, m("div", {
      className: "DHGuidePage-top"
    }, m("div", {
      className: "DHGuideBar"
    }, m("a", {
      className: "DataHubPub-back",
      href: "/data-hub",
      onclick: e => go(e, '/data-hub')
    }, m("i", {
      className: "fas fa-arrow-left"
    }), " Data Hub")), m("div", {
      className: "DHGuideIndex-head"
    }, m("div", {
      className: "DHHero-eyebrow"
    }, "Location guides"), m("h1", null, "Know the country before you land"), m("p", {
      className: "DHMuted"
    }, "History, communities, income and what to expect. Pick a country."))), m("div", {
      className: "DHGuideIndex"
    }, !guides ? m(LoadingIndicator, null) : guides.map(g => m("a", {
      className: "DHGuideIndex-card",
      href: '/data-hub/guides/' + g.slug,
      title: g.description,
      "aria-label": g.name + ' location guide',
      onclick: e => go(e, '/data-hub/guides/' + g.slug)
    }, g.shape ? m("svg", {
      className: "DHGuideIndex-shape",
      viewBox: g.shape.viewBox,
      "aria-hidden": "true",
      preserveAspectRatio: "xMidYMid meet"
    }, m("path", {
      d: g.shape.d
    })) : null, m("span", {
      className: "DHGuideIndex-name"
    }, g.name), m("span", {
      className: "DHGuideIndex-go"
    }, "Read the guide ", m("i", {
      className: "fas fa-arrow-right"
    }))))));
  }
}